import React from "react";
import { Box, Checkbox, Grid, Typography, useTheme } from "@mui/material";

export interface DashboardCustomizeItem {
  id: string;
  label: string;
}

interface DashboardCustomizeWidgetListProps {
  items: DashboardCustomizeItem[];
  selectedIds: string[];
  searchQuery: string;
  selectAllLabel: string;
  onChange: (next: string[]) => void;
}

const DashboardCustomizeWidgetList: React.FC<DashboardCustomizeWidgetListProps> = ({
  items,
  selectedIds,
  searchQuery,
  selectAllLabel,
  onChange,
}) => {
  const theme = useTheme();
  const query = searchQuery.trim().toLowerCase();
  const visible = query
    ? items.filter((item) => item.label.toLowerCase().includes(query))
    : items;
  const selectedSet = new Set(selectedIds);
  const allSelected = items.length > 0 && items.every((item) => selectedSet.has(item.id));
  const someSelected = items.some((item) => selectedSet.has(item.id));

  return (
    <>
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: "10px",
          backgroundColor: theme.palette.action.hover,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 500 }}>{selectAllLabel}</Typography>
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={(event) =>
            onChange(event.target.checked ? items.map((item) => item.id) : [])
          }
          inputProps={{ "aria-label": selectAllLabel }}
        />
      </Box>
      <Grid container spacing={1}>
        {visible.map((item) => {
          const checked = selectedSet.has(item.id);
          return (
            <Grid item xs={12} sm={6} key={item.id}>
              <Box
                onClick={() =>
                  onChange(
                    checked
                      ? selectedIds.filter((id) => id !== item.id)
                      : [...selectedIds, item.id],
                  )
                }
                sx={{
                  p: 1.75,
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: checked ? theme.palette.primary.main : theme.palette.divider,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    boxShadow: `0 4px 12px ${theme.palette.action.hover}`,
                  },
                }}
              >
                <Checkbox
                  checked={checked}
                  sx={{
                    p: 0,
                    pointerEvents: "none",
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, userSelect: "none", flexGrow: 1 }}
                >
                  {item.label}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default DashboardCustomizeWidgetList;
